import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BookOpen, ArrowRight, Thermometer, Droplets, Search, X, GitCompare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIFFICULTY_COLORS = {
  Beginner: "text-green-400 border-green-400/30 bg-green-400/10",
  "Beginner-Intermediate": "text-amber-400 border-amber-400/30 bg-amber-400/10",
  Intermediate: "text-amber-400 border-amber-400/30 bg-amber-400/10",
  "Intermediate (hibernation knowledge required)": "text-amber-400 border-amber-400/30 bg-amber-400/10",
  Advanced: "text-red-400 border-red-400/30 bg-red-400/10",
};

const GROUPS = ["All", "Geckos", "Dragons & Lizards", "Snakes", "Monitors & Tegus", "Tortoises", "Amphibians"];
const DIFFICULTIES = ["All", "Beginner", "Intermediate", "Advanced"];
const HEAT_SOURCES = [
  { key: "all", label: "All" },
  { key: "bulb", label: "Basking bulb" },
  { key: "mat", label: "Heat mat" },
  { key: "none", label: "Ambient only" },
];

// Normalises mixed difficulty strings ("Beginner-Intermediate", "Intermediate (hibernation knowledge required)")
// to a primary bucket for filtering.
const primaryDifficulty = (d) => {
  if (!d) return "";
  const lower = d.toLowerCase();
  if (lower.startsWith("beginner")) return "Beginner";
  if (lower.startsWith("intermediate")) return "Intermediate";
  if (lower.startsWith("advanced")) return "Advanced";
  return "Other";
};

const GuideIndexPage = () => {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [group, setGroup] = useState("All");
  const [difficulty, setDifficulty] = useState("All");
  const [heatSource, setHeatSource] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/guides`);
        setGuides(data.guides || []);
      } catch (e) {
        console.error("Failed to load guides:", e?.message || e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return guides.filter((g) => {
      if (q && !g.common_name.toLowerCase().includes(q) && !g.latin_name.toLowerCase().includes(q)) return false;
      if (group !== "All" && g.group !== group) return false;
      if (difficulty !== "All" && primaryDifficulty(g.difficulty) !== difficulty) return false;
      if (heatSource !== "all" && g.heat_source !== heatSource) return false;
      return true;
    });
  }, [guides, query, group, difficulty, heatSource]);

  const hasActiveFilter = query || group !== "All" || difficulty !== "All" || heatSource !== "all";
  const clearFilters = () => {
    setQuery("");
    setGroup("All");
    setDifficulty("All");
    setHeatSource("all");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F4F1DE] pt-24 pb-32 md:pb-16" data-testid="guides-index-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <div className="mb-10 max-w-3xl flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-[#E07A5F] text-xs uppercase tracking-widest mb-3">
              <BookOpen className="w-4 h-4" />
              <span>UK species care library</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#F4F1DE] leading-tight mb-4">
              Keep your reptile thriving, <span className="text-[#E07A5F]">not just alive.</span>
            </h1>
            <p className="text-base text-[#F4F1DE]/70 leading-relaxed">
              Every care guide below is written against modern UK husbandry standards and
              cross-referenced to primary sources. If a number isn't verified, it isn't here.
            </p>
          </div>
          <Link
            to="/guides/compare"
            className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 border border-[#E07A5F]/50 text-[#E07A5F] hover:bg-[#E07A5F] hover:text-white transition-colors whitespace-nowrap text-sm font-semibold uppercase tracking-widest"
            data-testid="compare-species-link"
          >
            <GitCompare className="w-4 h-4" />
            Compare 2 species
          </Link>
        </div>

        {/* Search + filters */}
        <div className="mb-6 space-y-3" data-testid="guides-filter-bar">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#F4F1DE]/40" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by common or Latin name"
              className="w-full bg-[#121212] border border-white/10 pl-10 pr-10 py-2.5 text-sm text-[#F4F1DE] placeholder-[#F4F1DE]/30 focus:border-[#E07A5F] focus:outline-none"
              data-testid="guides-search-input"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#F4F1DE]/40 hover:text-[#E07A5F]"
                data-testid="guides-search-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <FilterRow label="Group" options={GROUPS} value={group} onChange={setGroup} testid="filter-group" />
          <FilterRow
            label="Difficulty"
            options={DIFFICULTIES}
            value={difficulty}
            onChange={setDifficulty}
            testid="filter-difficulty"
          />
          <FilterRow
            label="Heat source"
            options={HEAT_SOURCES.map((h) => h.label)}
            optionKeys={HEAT_SOURCES.map((h) => h.key)}
            value={heatSource}
            onChange={setHeatSource}
            testid="filter-heat"
          />

          <div className="flex items-center justify-between text-xs text-[#F4F1DE]/50 pt-1">
            <span data-testid="guides-result-count">
              {filtered.length} of {guides.length} {guides.length === 1 ? "species" : "species"}
            </span>
            {hasActiveFilter && (
              <button
                onClick={clearFilters}
                className="text-[#E07A5F] hover:underline uppercase tracking-widest"
                data-testid="clear-filters-btn"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-[#F4F1DE]/50" data-testid="guides-empty-state">
            <p className="text-sm">No species match those filters.</p>
            {hasActiveFilter && (
              <button
                onClick={clearFilters}
                className="mt-3 text-[#E07A5F] hover:underline text-xs uppercase tracking-widest"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="guides-grid">
            {filtered.map((g) => (
              <Link
                to={`/guides/${g.slug}`}
                key={g.slug}
                className="group flex gap-4 p-4 border border-white/10 hover:border-[#E07A5F]/50 bg-[#121212] transition-all hover:-translate-y-0.5"
                data-testid={`guide-card-${g.slug}`}
              >
                {g.image_url && (
                  <div className="shrink-0 w-24 h-24 sm:w-28 sm:h-28 overflow-hidden border border-white/10 bg-[#0A0A0A]" data-testid={`guide-thumb-${g.slug}`}>
                    <img
                      src={g.image_url}
                      alt={g.common_name}
                      loading="lazy"
                      onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <p className="text-lg font-bold text-[#F4F1DE] leading-tight group-hover:text-[#E07A5F] transition-colors truncate">
                        {g.common_name}
                      </p>
                      <p className="text-xs italic text-[#F4F1DE]/40 mt-0.5 truncate">{g.latin_name}</p>
                    </div>
                    <span
                      className={`text-[10px] px-2 py-0.5 border rounded uppercase tracking-wider shrink-0 ${
                        DIFFICULTY_COLORS[g.difficulty] || "text-[#F4F1DE]/60 border-white/20"
                      }`}
                    >
                      {g.difficulty.split(" ")[0]}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[11px] text-[#F4F1DE]/60">
                    <span>📦 {g.enclosure_min_ft}</span>
                    <span className="flex items-center gap-1">
                      <Thermometer className="w-3 h-3 text-amber-400" />
                      {g.temp_basking_surface_c}°C basking
                    </span>
                    <span className="flex items-center gap-1">
                      <Droplets className="w-3 h-3 text-blue-400" />
                      {g.humidity_percent.split(",")[0]}
                    </span>
                  </div>
                  <div className="mt-3 flex items-center gap-1 text-xs text-[#E07A5F] group-hover:gap-2 transition-all">
                    Read full guide <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        <p className="mt-10 text-xs text-[#F4F1DE]/40 leading-relaxed max-w-3xl">
          <strong className="text-[#F4F1DE]/60">Please note:</strong> we verify husbandry data against
          at least two authoritative sources before publishing a guide. These guides are informational — always
          consult a qualified UK reptile vet for health concerns.
        </p>
      </div>

      {/* Sticky mobile Compare CTA — desktop already has it inline up top */}
      <div
        className="md:hidden fixed bottom-0 inset-x-0 z-30 px-4 pb-[max(env(safe-area-inset-bottom),12px)] pt-3 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/95 to-transparent"
        data-testid="mobile-compare-bar"
      >
        <Link
          to="/guides/compare"
          className="flex items-center justify-center gap-2 w-full py-3.5 bg-[#E07A5F] text-white font-semibold uppercase tracking-widest text-xs shadow-[0_-4px_24px_rgba(224,122,95,0.35)] hover:bg-[#D06A4F] active:scale-[0.99] transition-all"
          data-testid="mobile-compare-cta"
        >
          <GitCompare className="w-4 h-4" />
          Compare 2 species
        </Link>
      </div>
    </div>
  );
};

const FilterRow = ({ label, options, optionKeys, value, onChange, testid }) => (
  <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid={testid}>
    <span className="shrink-0 text-[10px] uppercase tracking-widest text-[#F4F1DE]/40 w-20 font-semibold">
      {label}
    </span>
    {options.map((opt, i) => {
      const key = optionKeys ? optionKeys[i] : opt;
      const active = value === key;
      return (
        <button
          key={key}
          onClick={() => onChange(key)}
          className={`shrink-0 text-[11px] uppercase tracking-wider px-3 py-1.5 border transition-colors ${
            active
              ? "bg-[#E07A5F] text-white border-[#E07A5F]"
              : "border-white/15 text-[#F4F1DE]/70 hover:border-[#E07A5F]/60 hover:text-[#E07A5F]"
          }`}
          data-testid={`${testid}-${key.toLowerCase().replace(/\s+/g, '-')}`}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

export default GuideIndexPage;
